import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import SEOHead from '@/components/feature/SEOHead';

const principles = [
  {
    icon: 'ri-heart-3-line',
    title: 'Patience & Understanding',
    desc: 'Every session is led with deep patience and genuine understanding of how neurodivergent minds think, process, and create.',
  },
  {
    icon: 'ri-user-heart-line',
    title: 'Personalized Development',
    desc: 'No two individuals are the same. We tailor our approach to each person\'s unique learning style, strengths, and goals.',
  },
  {
    icon: 'ri-shield-star-line',
    title: 'Safe & Structured Space',
    desc: 'A predictable, calm, and welcoming environment where every individual feels secure enough to open up, explore, and grow.',
  },
  {
    icon: 'ri-seedling-line',
    title: 'Confidence Restored',
    desc: 'We focus on rebuilding confidence through creative achievement — celebrating every milestone, no matter the size.',
  },
  {
    icon: 'ri-lightbulb-line',
    title: 'Creativity as Expression',
    desc: 'For many neurodivergent individuals, the arts become a powerful voice. We honor and develop that expression with purpose.',
  },
  {
    icon: 'ri-group-line',
    title: 'Inclusive Community',
    desc: 'You are not overlooked here. You are seen, celebrated, and surrounded by others who believe in your potential.',
  },
];

const intakeSteps = [
  {
    number: '01',
    icon: 'ri-phone-line',
    title: 'Reach Out',
    description: 'Start with a simple call, message, or in-person visit. No forms, no commitments — just an open and honest conversation about your individual and what you are hoping for.',
    note: 'We are here to listen first.',
    color: 'from-[#1E3A8A] to-[#3730A3]',
  },
  {
    number: '02',
    icon: 'ri-discuss-line',
    title: 'Free Family Consultation',
    description: 'We sit down with you and your family — or one-on-one with the individual if they prefer — to understand their learning style, communication preferences, creative interests, and any sensitivities we should know about.',
    note: 'Families are always welcome and encouraged to be part of this step.',
    color: 'from-[#3730A3] to-[#5B21B6]',
  },
  {
    number: '03',
    icon: 'ri-file-list-3-line',
    title: 'Personalized Plan Built',
    description: 'Using everything we learned, we design a fully custom development plan — specific to your individual\'s pace, strengths, and goals. No cookie-cutter programs. No one-size-fits-all.',
    note: 'You review the plan before any session begins.',
    color: 'from-[#5B21B6] to-[#7C3AED]',
  },
  {
    number: '04',
    icon: 'ri-seedling-line',
    title: 'Gentle First Session',
    description: 'The first session is low-pressure and exploratory. We let your individual lead. We observe, connect, and build trust. There is no rush and no expectation — only a safe space to begin.',
    note: 'If they are not ready to play, that is perfectly okay.',
    color: 'from-[#7C3AED] to-[#6D28D9]',
  },
  {
    number: '05',
    icon: 'ri-bar-chart-grouped-line',
    title: 'Ongoing Growth & Check-ins',
    description: 'Sessions continue at a consistent, comfortable cadence. We track progress and share milestones with the family. If something is not working, we adjust — quickly and without judgment.',
    note: 'You will always know how your individual is growing.',
    color: 'from-[#1E3A8A] to-[#2D1B69]',
  },
];

const reassurances = [
  { icon: 'ri-shield-check-line', label: 'Zero Judgment', detail: 'Every background and ability is welcome here.' },
  { icon: 'ri-time-line', label: 'Your Pace Always', detail: 'We never rush. Progress looks different for everyone.' },
  { icon: 'ri-group-2-line', label: 'Family Included', detail: 'Parents and guardians are partners, not bystanders.' },
  { icon: 'ri-calendar-check-line', label: 'Flexible Scheduling', detail: 'Evening and weekend slots available for all families.' },
  { icon: 'ri-chat-heart-line', label: 'Open Communication', detail: 'We update families regularly — not just when there\'s a concern.' },
  { icon: 'ri-star-smile-line', label: 'Every Win Celebrated', detail: 'Big or small, milestones matter and we honor them all.' },
];

const neuroJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Neurodivergent Creative Development',
  url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/neurodivergent-development`,
  description: 'Creative Institute LLC provides specialized creative development programs for neurodivergent individuals — ADHD, autism, dyslexia, and more. Safe, structured, and empowering.',
  provider: {
    '@type': 'EducationalOrganization',
    name: 'Creative Institute LLC',
    telephone: '910-242-4567',
  },
  audience: { '@type': 'Audience', audienceType: 'Neurodivergent individuals, children with ADHD, autism spectrum, dyslexia' },
  areaServed: { '@type': 'State', name: 'North Carolina' },
};

function HeroSection() {
  return (
    <section className="relative min-h-[70vh] flex items-center overflow-hidden">
      <div className="absolute inset-0 z-0">
        <img
          src="https://public.readdy.ai/ai/img_res/7ae7996c1e6c0102e580faec7dcecb7b.jpg"
          alt="Neurodivergent Creative Development environment"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1E3A8A]/90 via-[#2D1B69]/80 to-[#5B21B6]/70" />
      </div>

      <div className="relative z-10 max-w-screen-xl mx-auto px-8 py-32 pt-40">
        <span
          className="inline-block px-4 py-1.5 rounded-full border border-brand-yellow/50 text-brand-yellow font-body text-xs uppercase tracking-widest bg-brand-yellow/10 mb-6"
          style={{ animation: 'wordFadeUp 0.7s cubic-bezier(0.22,1,0.36,1) both', animationDelay: '0.1s' }}
        >
          Neurodivergent Creative Development
        </span>
        <h1 className="font-heading text-white text-5xl lg:text-7xl font-black leading-tight mb-6 max-w-4xl">
          <span
            className="block"
            style={{ animation: 'wordFadeUp 0.7s cubic-bezier(0.22,1,0.36,1) both', animationDelay: '0.3s' }}
          >
            Built for Those Who Are
          </span>
          <span
            className="block text-brand-yellow"
            style={{ animation: 'wordFadeUp 0.7s cubic-bezier(0.22,1,0.36,1) both', animationDelay: '0.55s' }}
          >
            Often Overlooked
          </span>
        </h1>
        <p
          className="font-body text-white/80 text-lg lg:text-xl max-w-2xl leading-relaxed mb-8"
          style={{ animation: 'wordFadeUp 0.7s cubic-bezier(0.22,1,0.36,1) both', animationDelay: '0.8s' }}
        >
          Creative Institute was built for those who are often overlooked, misunderstood, or placed in environments that were not designed for how they learn and grow.
        </p>
        <div
          className="flex flex-wrap gap-4"
          style={{ animation: 'wordFadeUp 0.7s cubic-bezier(0.22,1,0.36,1) both', animationDelay: '1.05s' }}
        >
          <Link to="/enroll" className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-4 rounded-full bg-brand-yellow text-brand-blue-dark font-body font-bold text-sm cursor-pointer hover:bg-brand-yellow-light transition-colors">
            <i className="ri-arrow-right-circle-line" /> Begin Your Journey
          </Link>
          <Link to="/contact" className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-4 rounded-full border-2 border-white/60 text-white font-body font-semibold text-sm cursor-pointer hover:border-white hover:bg-white/10 transition-colors">
            <i className="ri-phone-line" /> Talk to Us
          </Link>
        </div>
      </div>

      <style>{`
        @keyframes wordFadeUp {
          0%   { opacity: 0; transform: translateY(14px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}

function MissionStatement() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });
  return (
    <section ref={ref} className="py-24 bg-gradient-to-br from-[#F5F3FF] via-white to-[#EEF2FF] relative overflow-hidden">
      <div className="absolute top-0 right-0 w-80 h-80 bg-[#DDD6FE] rounded-full blur-3xl opacity-30 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#EEF2FF] rounded-full blur-3xl opacity-40 pointer-events-none" />
      <div className="max-w-screen-xl mx-auto px-8">
        <div className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} max-w-4xl mx-auto text-center`}>
          <h2 className="font-heading text-4xl lg:text-5xl font-black text-brand-blue-dark mb-8">
            This Is More Than a Place to Learn.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#1E3A8A] to-[#5B21B6]">It Is a Space to Thrive.</span>
          </h2>
          <p className="font-body text-gray-600 text-lg leading-loose mb-6">
            Here, we believe every individual deserves patience, understanding, and the opportunity to be developed with care. We create an environment where confidence is restored, creativity is nurtured, and every person is given the chance to thrive.
          </p>
          <p className="font-body text-gray-500 text-base leading-loose">
            Creative Institute is committed to creating a safe, structured, and empowering space for neurodivergent individuals — where every person is given the opportunity to grow, express themselves, and develop into who they are meant to be.
          </p>
        </div>
      </div>
    </section>
  );
}

function VideoSection() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <section ref={ref} className="py-24 bg-gradient-to-b from-[#F8F7FF] to-white">
      <div className="max-w-screen-xl mx-auto px-8">

        {/* Header */}
        <div className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} text-center mb-14`}>
          <span className="inline-block px-4 py-1.5 rounded-full bg-brand-yellow/15 text-brand-yellow-dark font-body text-xs uppercase tracking-widest font-semibold border border-brand-yellow/30 mb-4">
            Our Approach
          </span>
          <h2 className="font-heading text-4xl lg:text-5xl font-black text-brand-blue-dark mb-4">
            Hear It From Our Founder
          </h2>
          <p className="font-body text-gray-500 text-base max-w-xl mx-auto">
            Watch this personal message from Jaquan Little explaining our approach to neurodivergent creative development.
          </p>
        </div>

        {/* Two-column: video + pull-quote */}
        <div className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-center max-w-6xl mx-auto`}>

          {/* Video — takes 3/5 */}
          <div className="lg:col-span-3">
            <div className="rounded-3xl overflow-hidden bg-black aspect-video">
              <video
                ref={videoRef}
                src="https://dl.dropboxusercontent.com/scl/fi/pd0d8yrz511vpw22pa85i/Video-Mar-27-2026-3-46-18-PM.mov?rlkey=z4bcauf0l9u9op8vchwve13w1"
                controls
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                className="w-full h-full object-contain"
              >
                Your browser does not support the video tag.
              </video>
            </div>
            <p className="font-body text-gray-400 text-xs mt-3 flex items-center gap-1">
              <i className="ri-user-line" />
              Jaquan Little — Founder, Creative Institute
            </p>
          </div>

          {/* Pull-quote — takes 2/5 */}
          <div className="lg:col-span-2 flex flex-col justify-center">
            <div className="relative pl-6 border-l-4 border-brand-yellow">
              <i className="ri-double-quotes-l text-5xl text-brand-yellow/30 absolute -top-3 -left-1 leading-none" />
              <blockquote className="font-heading text-brand-blue-dark text-2xl lg:text-3xl font-black leading-snug mb-6">
                Every child deserves to be seen for who they are — not who the system expects them to be.
              </blockquote>
              <p className="font-body text-gray-500 text-sm leading-relaxed mb-8">
                We don&apos;t change neurodivergent individuals to fit a mold. We build an environment where they can finally be themselves — and thrive because of it.
              </p>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-br from-[#1E3A8A] to-[#5B21B6] text-white flex-shrink-0">
                  <i className="ri-user-line text-sm" />
                </div>
                <div>
                  <p className="font-heading text-brand-blue-dark text-sm font-bold">Jaquan Little</p>
                  <p className="font-body text-gray-400 text-xs">Founder, Creative Institute</p>
                </div>
              </div>
              <Link
                to="/enroll"
                className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-yellow text-brand-blue-dark font-body font-bold text-sm cursor-pointer hover:bg-brand-yellow-light transition-colors"
              >
                <i className="ri-arrow-right-circle-line" /> Begin Your Journey
              </Link>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}

function PrinciplesSection() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.05 });
  return (
    <section ref={ref} className="py-24 bg-gradient-to-br from-[#EEF2FF]/40 via-white to-[#F5F3FF]/40">
      <div className="max-w-screen-xl mx-auto px-8">
        <div className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} text-center mb-16`}>
          <h2 className="font-heading text-4xl lg:text-5xl font-black text-brand-blue-dark mb-4">
            How We Show Up for You
          </h2>
          <p className="font-body text-gray-500 text-base max-w-2xl mx-auto">
            Our commitment to neurodivergent individuals is woven into every session, every interaction, and every decision we make.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {principles.map((item, i) => (
            <div
              key={item.title}
              className={`scroll-hidden-blur ${isVisible ? 'scroll-visible' : ''} p-8 rounded-2xl bg-white border border-[#DDD6FE]/60 card-lift`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-gradient-to-br from-[#1E3A8A] to-[#5B21B6] text-white mb-6 wiggle-hover">
                <i className={`${item.icon} text-2xl`} />
              </div>
              <h3 className="font-heading text-brand-blue-dark text-xl font-bold mb-3">{item.title}</h3>
              <p className="font-body text-gray-500 text-sm leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhatToExpectSection() {
  const { ref: titleRef, isVisible: titleVis } = useScrollAnimation({ threshold: 0.15 });
  const { ref: stepsRef, isVisible: stepsVis } = useScrollAnimation({ threshold: 0.05 });
  const { ref: reassureRef, isVisible: reassureVis } = useScrollAnimation({ threshold: 0.1 });

  return (
    <section className="py-28 bg-gradient-to-b from-white via-[#F5F3FF]/60 to-[#EEF2FF]/80 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-96 h-96 bg-[#DDD6FE]/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-20 right-0 w-80 h-80 bg-[#C7D2FE]/20 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-screen-xl mx-auto px-8">

        {/* Section Header */}
        <div ref={titleRef} className="text-center mb-20">
          <span className={`scroll-hidden ${titleVis ? 'scroll-visible' : ''} inline-block px-4 py-1.5 rounded-full bg-brand-yellow/15 text-brand-yellow-dark font-body text-xs uppercase tracking-widest font-semibold border border-brand-yellow/30 mb-5`}>
            The Intake Process
          </span>
          <h2 className={`scroll-hidden delay-100 ${titleVis ? 'scroll-visible' : ''} font-heading text-5xl lg:text-6xl font-black text-brand-blue-dark mb-5 leading-tight`}>
            What to Expect When<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#1E3A8A] to-[#7C3AED]">You Come to Us</span>
          </h2>
          <p className={`scroll-hidden delay-200 ${titleVis ? 'scroll-visible' : ''} font-body text-gray-500 text-lg max-w-2xl mx-auto leading-relaxed`}>
            We know starting somewhere new can feel uncertain — especially when your individual has had difficult experiences before. Here is exactly how it works, step by step.
          </p>
        </div>

        {/* Timeline Steps */}
        <div ref={stepsRef} className="relative max-w-4xl mx-auto mb-20">
          {/* Vertical connector line */}
          <div className="absolute left-[28px] top-10 bottom-10 w-px bg-gradient-to-b from-[#1E3A8A]/20 via-[#5B21B6]/30 to-[#7C3AED]/20 hidden lg:block" />

          <div className="flex flex-col gap-8">
            {intakeSteps.map((step, i) => (
              <div
                key={step.number}
                className={`scroll-hidden ${stepsVis ? 'scroll-visible' : ''} flex gap-8 items-start group`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                {/* Step badge */}
                <div className="flex-shrink-0 flex flex-col items-center gap-2">
                  <div className={`w-14 h-14 flex items-center justify-center rounded-2xl bg-gradient-to-br ${step.color} text-white font-heading font-black text-base`}>
                    {step.number}
                  </div>
                </div>

                {/* Card */}
                <div className="flex-1 bg-white rounded-2xl border border-[#DDD6FE]/50 p-8 card-lift relative overflow-hidden">
                  <div className={`absolute top-0 left-0 w-1 h-full bg-gradient-to-b ${step.color} rounded-l-2xl`} />
                  <div className="flex items-start gap-4 mb-4">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br ${step.color} text-white flex-shrink-0`}>
                      <i className={`${step.icon} text-base`} />
                    </div>
                    <h3 className="font-heading text-brand-blue-dark text-xl font-bold mt-1">{step.title}</h3>
                  </div>
                  <p className="font-body text-gray-500 text-sm leading-relaxed mb-4">{step.description}</p>
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-yellow/10 border border-brand-yellow/30">
                    <i className="ri-checkbox-circle-line text-brand-yellow-dark text-sm" />
                    <span className="font-body text-brand-yellow-dark text-xs font-medium">{step.note}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Reassurance Strip */}
        <div ref={reassureRef} className="rounded-3xl bg-gradient-to-br from-[#1E3A8A] to-[#5B21B6] p-12 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-72 h-72 bg-brand-yellow/8 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-56 h-56 bg-white/5 rounded-full blur-2xl pointer-events-none" />

          <div className={`scroll-hidden ${reassureVis ? 'scroll-visible' : ''} text-center mb-10 relative z-10`}>
            <h3 className="font-heading text-white text-3xl lg:text-4xl font-black mb-3">
              Our Promise to Every Family
            </h3>
            <p className="font-body text-white/65 text-base max-w-xl mx-auto">
              These are not just values on a wall — they are the standard we hold ourselves to in every single session.
            </p>
          </div>

          <div className="relative z-10 grid grid-cols-2 lg:grid-cols-3 gap-5">
            {reassurances.map((r, i) => (
              <div
                key={r.label}
                className={`scroll-hidden ${reassureVis ? 'scroll-visible' : ''} flex items-start gap-4 p-5 rounded-xl bg-white/8 border border-white/12 backdrop-blur-sm`}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-brand-yellow/20 text-brand-yellow flex-shrink-0">
                  <i className={`${r.icon} text-lg`} />
                </div>
                <div>
                  <p className="font-heading text-white text-sm font-bold mb-1">{r.label}</p>
                  <p className="font-body text-white/55 text-xs leading-snug">{r.detail}</p>
                </div>
              </div>
            ))}
          </div>

          <div className={`scroll-hidden ${reassureVis ? 'scroll-visible' : ''} relative z-10 text-center mt-10`}>
            <Link
              to="/contact"
              className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-4 rounded-full bg-brand-yellow text-brand-blue-dark font-body font-bold text-sm cursor-pointer hover:bg-brand-yellow-light transition-colors"
            >
              <i className="ri-phone-line" /> Schedule Your Free Consultation
            </Link>
          </div>
        </div>

      </div>
    </section>
  );
}

function CTABanner() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.2 });
  return (
    <section ref={ref} className="py-24 bg-gradient-to-r from-[#1E3A8A] via-[#2D1B69] to-[#5B21B6] relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-brand-yellow/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full blur-2xl pointer-events-none" />
      <div className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} relative z-10 max-w-3xl mx-auto px-8 text-center`}>
        <h2 className="font-heading text-white text-4xl lg:text-5xl font-black mb-5">
          You Belong Here
        </h2>
        <p className="font-body text-white/75 text-lg leading-relaxed mb-8">
          If you or someone you love has felt overlooked or unsupported in other environments — this is your space. We are ready to meet you exactly where you are.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/enroll" className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-4 rounded-full bg-brand-yellow text-brand-blue-dark font-body font-bold text-base cursor-pointer hover:bg-brand-yellow-light transition-colors">
            <i className="ri-arrow-right-circle-line" /> Start Your Journey
          </Link>
          <Link to="/in-home-programs" className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-4 rounded-full border-2 border-white/50 text-white font-body font-semibold text-base cursor-pointer hover:border-white hover:bg-white/10 transition-colors">
            <i className="ri-home-heart-line" /> In-Home Programs
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function NeurodivergentDevelopmentPage() {
  return (
    <main>
      <SEOHead
        title="Neurodivergent Creative Development | Creative Institute LLC NC"
        description="Creative Institute LLC offers specialized music and arts programs for neurodivergent individuals across North Carolina. ADHD, autism, dyslexia-friendly. Safe, structured, and empowering."
        keywords="neurodivergent music lessons NC, ADHD arts program, autism music education, creative development neurodivergent, inclusive music lessons North Carolina"
        canonical="/neurodivergent-development"
        jsonLd={neuroJsonLd}
      />
      <HeroSection />
      <MissionStatement />
      <VideoSection />
      <PrinciplesSection />
      <WhatToExpectSection />
      <CTABanner />
    </main>
  );
}
